import { styled, useTheme } from '@mui/material';
import isNumber from 'lodash/isNumber';
import round from 'lodash/round';

import { Icons, Typography, Stack } from '@cast/design-system';
import { ComputeResourceType } from '@cast/shared-types';

import { computeResourceUnitsMap } from 'constants/maps';
import { OptimizationNotEnabledTooltip } from 'features/cluster/workload-optimization/_components/OptimizationNotEnabledTooltip';
import {
  PreparedWorkload,
  ResourceMetrics,
} from 'features/workload-optimization';

import { isResourcePositive } from '../_utils';

const ArrowIcon = styled(Icons.ArrowRight)(({ theme }) => ({
  color: theme.palette.grey[400],
  fontSize: 16,
  pr: 2,
}));

const EqualsIcon = styled(Icons.Equals)(({ theme }) => ({
  color: theme.palette.green[500],
  fontSize: 16,
  pr: 2,
}));

type ResourceMetricsProps = {
  metrics: ResourceMetrics;
  unit: ComputeResourceType;
};

const RequestedMetricsRow = ({ metrics, unit }: ResourceMetricsProps) => {
  const { requested: _requested, isReadOnly } = metrics;
  const requested = _requested ? round(_requested, 3) : undefined;
  const isPositive = isResourcePositive(metrics);

  return (
    <OptimizationNotEnabledTooltip unit={unit} wrapIf={isReadOnly}>
      <Typography
        variant={isPositive ? 'P12M' : 'P12R'}
        color={
          !isNumber(requested)
            ? 'grey.400'
            : isPositive
            ? 'green.500'
            : 'grey.900'
        }
        {...{ [`${unit}-value`]: requested || 0 }}
      >
        {requested ? requested : 'N/A'}
      </Typography>
    </OptimizationNotEnabledTooltip>
  );
};

export const RequestedMetricsCell = ({ cpu, memory }: PreparedWorkload) => {
  return (
    <Stack>
      <RequestedMetricsRow metrics={cpu} unit={ComputeResourceType.CPU} />
      <RequestedMetricsRow metrics={memory} unit={ComputeResourceType.MEMORY} />
    </Stack>
  );
};

const RecommendedMetricsRow = ({ metrics, unit }: ResourceMetricsProps) => {
  const theme = useTheme();

  const { recommended: _recommended, isReadOnly } = metrics;
  const recommended = _recommended ? round(_recommended, 3) : undefined;
  const isPositive = isResourcePositive(metrics);

  return (
    <OptimizationNotEnabledTooltip unit={unit} wrapIf={isReadOnly}>
      <Stack direction="row" gap={6} alignItems="center" data-testid={unit}>
        {isPositive ? (
          <EqualsIcon data-testid="positive-icon" />
        ) : (
          <ArrowIcon data-testid="negative-icon" />
        )}
        <Typography
          className="woop-premium-value"
          variant={isPositive ? 'P12M' : 'P12R'}
          color={
            !isNumber(recommended)
              ? 'grey.400'
              : recommended && isReadOnly
              ? 'grey.300'
              : isPositive
              ? 'green.500'
              : 'grey.900'
          }
          sx={
            (isReadOnly && {
              textDecoration: 'underline dashed',
              textUnderlinePosition: 'under',
              textDecorationColor: theme.palette.grey[400],
            }) ||
            undefined
          }
          {...{ [`${unit}-value`]: recommended || 0 }}
        >
          {recommended ? recommended : 'N/A'}
        </Typography>
        <Typography
          variant={isPositive ? 'P12M' : 'P12R'}
          color={
            isReadOnly ? 'grey.300' : isPositive ? 'green.500' : 'grey.400'
          }
        >
          {computeResourceUnitsMap[unit]}
        </Typography>
      </Stack>
    </OptimizationNotEnabledTooltip>
  );
};

export const RecommendedMetricsCell = ({ cpu, memory }: PreparedWorkload) => {
  return (
    <Stack>
      <RecommendedMetricsRow metrics={cpu} unit={ComputeResourceType.CPU} />
      <RecommendedMetricsRow
        metrics={memory}
        unit={ComputeResourceType.MEMORY}
      />
    </Stack>
  );
};
